import React from 'react';

import axios from 'helpers/axios';
import config from 'helpers/config';

import Tenant from 'models/TenantModel';
import FormModes from 'helpers/FormModes';
import { Building2, Save, Edit, X, Globe, Mail, Phone, Clock, CreditCard, FileText, Info } from 'lucide-react';

interface Props {}

interface State {
    companySettings: any;
    tenant: Tenant;
    formState: FormModes;
    isLoading: boolean;
    beforeEdit: any;
}

class CompanySettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            companySettings: {},
            tenant: {} as Tenant,
            formState: FormModes.Preview,
            isLoading: false,
            beforeEdit: {},
        };

        this.getValue = this.getValue.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }

    componentDidMount() {
        this.getCompanySettings();
    }

    getCompanySettings() {
        axios
            .get(`${config.apiUrl}/tenant/settings`)
            .then((res) => this.setState({ tenant: res.data, companySettings: res.data.tenantSettings }));
    }

    getValue(name: string) {
        // @ts-ignore
        return this.state?.companySettings[name] || '';
    }

    handleChange(event: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>) {
        const { name, value } = event.currentTarget;

        const updateCompanySettings: any = { ...this.state.companySettings, [name]: value };
        this.setState({ companySettings: updateCompanySettings });
    }

    handleUpdate() {
        const { companySettings } = this.state;
        this.setState({ isLoading: true });
        axios
            .put(`${config.apiUrl}/tenant`, companySettings)
            .finally(() => this.setState({ isLoading: false, formState: FormModes.Preview }));
    }
    render() {
        const { tenant, formState, companySettings } = this.state;

        const fieldGroups = [
            {
                title: 'Business Information',
                icon: Building2,
                fields: [
                    {
                        name: 'businessName',
                        label: 'Company Name',
                        type: 'text',
                        value: tenant.businessName || '',
                        placeholder: 'Enter your company name',
                        required: true,
                        icon: Building2,
                    },
                    {
                        name: 'website',
                        label: 'Website',
                        type: 'url',
                        value: this.getValue('website'),
                        placeholder: 'https://www.yourcompany.com',
                        icon: Globe,
                    },
                    {
                        name: 'businessLicenseId',
                        label: 'Business License ID',
                        type: 'text',
                        value: this.getValue('businessLicenseId'),
                        placeholder: 'Enter business license number',
                        icon: FileText,
                    },
                ],
            },
            {
                title: 'Contact Information',
                icon: Phone,
                fields: [
                    {
                        name: 'primaryEmail',
                        label: 'Email Address',
                        type: 'email',
                        value: this.getValue('primaryEmail'),
                        placeholder: 'info@yourcompany.com',
                        icon: Mail,
                    },
                    {
                        name: 'phoneNumber',
                        label: 'Phone Number',
                        type: 'tel',
                        value: this.getValue('phoneNumber'),
                        placeholder: '+1 (555) 123-4567',
                        required: true,
                        icon: Phone,
                    },
                ],
            },
            {
                title: 'Business Operations',
                icon: Clock,
                fields: [
                    {
                        name: 'currency',
                        label: 'Currency',
                        type: 'text',
                        value: this.getValue('currency'),
                        placeholder: 'USD',
                        icon: CreditCard,
                    },
                    {
                        name: 'shopOpeningTime',
                        label: 'Opening Time',
                        type: 'time',
                        value: this.getValue('shopOpeningTime'),
                        placeholder: '09:00',
                        icon: Clock,
                    },
                    {
                        name: 'shopClosingTime',
                        label: 'Closing Time',
                        type: 'time',
                        value: this.getValue('shopClosingTime'),
                        placeholder: '17:00',
                        icon: Clock,
                    },
                ],
            },
        ];

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Company</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Company Settings</h2>
                            <p className="text-gray-600 mt-1">
                                Manage your company's basic information, contact details, and business operations.
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            {formState === FormModes.Preview && (
                                <button
                                    onClick={() => {
                                        this.setState({ formState: FormModes.Edit, beforeEdit: this.state });
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                >
                                    <Edit className="h-4 w-4" />
                                    <span>Edit Settings</span>
                                </button>
                            )}

                            {formState === FormModes.Edit && (
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => {
                                            this.setState({ ...this.state.beforeEdit, beforeEdit: {} });
                                        }}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                        <span>Cancel</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            this.handleUpdate();
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <Save className="h-4 w-4" />
                                        <span>Save Changes</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    {/* Status Banner */}
                    <div className="mb-8">
                        <div
                            className={`rounded-lg border p-4 ${
                                formState === FormModes.Edit
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-gray-50 border-gray-200'
                            }`}
                        >
                            <div className="flex items-center">
                                <div
                                    className={`p-2 rounded-lg ${
                                        formState === FormModes.Edit ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}
                                >
                                    <Building2
                                        className={`h-5 w-5 ${
                                            formState === FormModes.Edit ? 'text-blue-600' : 'text-gray-600'
                                        }`}
                                    />
                                </div>
                                <div className="ml-4">
                                    <h3
                                        className={`text-lg font-semibold ${
                                            formState === FormModes.Edit ? 'text-blue-900' : 'text-gray-900'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Editing Company Information'
                                            : 'Company Information Overview'}
                                    </h3>
                                    <p
                                        className={`text-sm ${
                                            formState === FormModes.Edit ? 'text-blue-700' : 'text-gray-600'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Make your changes below and click Save Changes to apply them.'
                                            : 'View your current company settings and business information.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Sections */}
                    <div className="space-y-8">
                        {fieldGroups.map((group, groupIndex) => {
                            const GroupIcon = group.icon;
                            return (
                                <div
                                    key={groupIndex}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                                >
                                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                        <div className="flex items-center">
                                            <GroupIcon className="h-5 w-5 text-gray-500 mr-3" />
                                            <h3 className="text-lg font-semibold text-gray-900">{group.title}</h3>
                                        </div>
                                    </div>

                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {group.fields.map((field, fieldIndex) => {
                                                const FieldIcon = field.icon;
                                                return (
                                                    <div key={fieldIndex} className="space-y-2">
                                                        <label className="flex items-center text-sm font-medium text-gray-700">
                                                            <FieldIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                            {field.label}
                                                            {field.required && (
                                                                <span className="text-red-500 ml-1">*</span>
                                                            )}
                                                        </label>
                                                        <input
                                                            type={field.type}
                                                            name={field.name}
                                                            value={field.value}
                                                            placeholder={field.placeholder}
                                                            onChange={this.handleChange}
                                                            disabled={formState === FormModes.Preview}
                                                            required={field.required}
                                                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                                formState === FormModes.Preview
                                                                    ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-not-allowed'
                                                                    : 'border-gray-300 hover:border-gray-400'
                                                            }`}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">Company Settings Information</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Company name and contact information appear on invoices and customer
                                        communications
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Business hours are used for appointment scheduling and customer expectations
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Currency setting affects how prices and fees are displayed throughout the system
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Business license information may be required for legal compliance and reporting
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default CompanySettings;
